import { Stack, Typography, IconButton, Box } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import DeleteIcon from "@mui/icons-material/Delete";
import type { TimeRange } from "Types/OpeningHours";
import {
  parseStringTimeToDate,
  formatDateTimeToTimeString,
} from "Utils/DateUtils";
import { Resources, useResource } from "Translations/Resources";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { checkTimeRangeValidity } from "Utils/TimeUtils";

const RESOURCES = Resources.Components.Merchant.Facilities.Form.OpeningHours;

const handleTimeChange =
  (
    key: keyof TimeRange,
    range: TimeRange,
    onChange: (range: TimeRange) => void,
  ) =>
  (value: Date | null) => {
    const isInvalidTime = Number.isNaN(value?.getTime());
    if (isInvalidTime) {
      return;
    }

    const newValue = value ? formatDateTimeToTimeString(value) : null;
    onChange({ ...range, [key]: newValue });
  };

type Props = {
  range: TimeRange;
  onChange: (range: TimeRange) => void;
  onDelete: () => void;
  disabled?: boolean;
};

export const TimeRangeInput = ({
  range,
  onChange,
  onDelete,
  disabled,
}: Props) => {
  const { t } = useResource();

  const handleOpenTimeChange = handleTimeChange("openTime", range, onChange);
  const handleCloseTimeChange = handleTimeChange("closeTime", range, onChange);
  const { openTime, closeTime } = range;

  const isTimeRangeValid = checkTimeRangeValidity(range);

  return (
    <Stack mb={1} gap={0.5}>
      <Stack gap={1} direction="row" alignItems="center" flexWrap="wrap">
        <Stack
          gap={[3, 2]}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ flex: { xs: 1, sm: "none" }, minWidth: 0 }}
        >
          <TimePicker
            label={t(RESOURCES.From)}
            value={openTime ? parseStringTimeToDate(openTime) : null}
            onChange={handleOpenTimeChange}
            disabled={disabled}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: { xs: "100%", sm: "140px" },
                  minWidth: { sm: "140px" },
                },
              },
            }}
          />
          <Typography sx={{ display: { xs: "none", sm: "block" } }}>
            -
          </Typography>
          <TimePicker
            label={t(RESOURCES.To)}
            value={closeTime ? parseStringTimeToDate(closeTime) : null}
            onChange={handleCloseTimeChange}
            disabled={disabled}
            slotProps={{
              textField: {
                error: !isTimeRangeValid,
                size: "small",
                sx: {
                  width: { xs: "100%", sm: "140px" },
                  minWidth: { sm: "140px" },
                },
              },
            }}
          />
        </Stack>
        <IconButton
          onClick={onDelete}
          disabled={disabled}
          size="small"
          color="error"
          sx={{ flexShrink: 0 }}
          aria-label={t(RESOURCES.DeleteTimeRange)}
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
      <Box minHeight={20}>
        {!isTimeRangeValid && (
          <ErrorText sx={{ fontSize: 12, fontWeight: 300, mt: 0.5 }}>
            {t(RESOURCES.Validation.OpeningTimeBeforeClosingTime)}
          </ErrorText>
        )}
      </Box>
    </Stack>
  );
};
